import React from "react";
import { Link } from "react-router-dom";
import { 
  LifebuoyIcon, 
  ChatBubbleLeftRightIcon, 
  DocumentTextIcon,
  PhoneIcon,
  EnvelopeIcon,
  QuestionMarkCircleIcon,
  ExclamationTriangleIcon,
  UserCircleIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  HandRaisedIcon
} from "@heroicons/react/24/outline";

const helpCategories = [
  {
    title: "Hesap Yönetimi",
    icon: UserCircleIcon,
    description: "Hesap oluşturma, profil düzenleme ve güvenlik ayarları",
    links: [
      { text: "Nasıl kayıt olabilirim?", href: "/faq" },
      { text: "Profilimi nasıl güncellerim?", href: "/faq" },
      { text: "Şifremi nasıl değiştirim?", href: "/faq" }
    ]
  },
  {
    title: "Eğitmenler İçin",
    icon: BookOpenIcon,
    description: "İlan oluşturma, takvim yönetimi ve talep onaylama",
    links: [
      { text: "İlan nasıl oluşturulur?", href: "/faq" },
      { text: "Takvimimi nasıl güncellerim?", href: "/faq" },
      { text: "Talepleri nasıl yönetirim?", href: "/faq" }
    ]
  },
  {
    title: "Öğrenciler İçin",
    icon: HandRaisedIcon,
    description: "Ders arama, talep oluşturma ve eğitmenle iletişim",
    links: [
      { text: "Nasıl ders bulabilirim?", href: "/faq" },
      { text: "Talep nasıl oluşturulur?", href: "/faq" },
      { text: "Eğitmenle nasıl iletişime geçerim?", href: "/faq" }
    ]
  },
  {
    title: "Teknik Destek",
    icon: ExclamationTriangleIcon,
    description: "Platform sorunları ve teknik yardım",
    links: [
      { text: "Giriş yapamıyorum", href: "/faq" },
      { text: "Sayfa yüklenmiyor", href: "/faq" },
      { text: "Bildirim alamıyorum", href: "/faq" }
    ]
  }
];

const quickGuides = [
  {
    title: "Yeni Eğitmen Rehberi",
    description: "İlk ilanınızı nasıl oluşturacağınızı öğrenin",
    icon: "👨‍🏫",
    steps: [
      "Hesap oluşturun ve profilinizi tamamlayın",
      "Uzmanlık alanlarınızı belirtin",
      "Takvimde müsait olduğunuz zamanları işaretleyin",
      "İlk ders ilanınızı oluşturun"
    ]
  },
  {
    title: "Yeni Öğrenci Rehberi", 
    description: "İhtiyacınız olan dersi nasıl bulacağınızı öğrenin",
    icon: "👨‍🎓",
    steps: [
      "Hesap oluşturun ve giriş yapın",
      "Arama ile size uygun dersleri bulun",
      "İlan detaylarını inceleyin",
      "Uygun tarihte talep oluşturun"
    ]
  }
];

const contactMethods = [
  {
    icon: EnvelopeIcon,
    title: "E-posta Desteği",
    description: "24 saat içinde yanıt alın",
    action: "destek@dersmaster.com",
    color: "bg-blue-600 hover:bg-blue-700",
    href: "mailto:destek@dersmaster.com"
  },
  {
    icon: ChatBubbleLeftRightIcon,
    title: "İletişim Formu",
    description: "Detaylı sorularınız için",
    action: "Form Doldur",
    color: "bg-green-600 hover:bg-green-700",
    href: "/contact"
  }
];

const Help = () => (
  <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white dark:from-gray-900 dark:to-gray-800">
    {/* Header */}
    <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-16">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <LifebuoyIcon className="h-16 w-16 mx-auto mb-4 text-purple-100" />
        <h1 className="text-4xl font-bold mb-4">Yardım Merkezi</h1>
        <p className="text-xl text-purple-100 max-w-2xl mx-auto">
          DersMaster özel ders platformunu nasıl kullanacağınızı öğrenin
        </p>
      </div>
    </div>

    <div className="max-w-6xl mx-auto py-12 px-4">
      {/* Quick Guides */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-center mb-8 text-gray-900 dark:text-white">
          Hızlı Başlangıç Rehberleri
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {quickGuides.map((guide, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
              <div className="text-center mb-4">
                <div className="text-4xl mb-2">{guide.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{guide.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">{guide.description}</p>
              </div>
              <ol className="space-y-2">
                {guide.steps.map((step, stepIndex) => (
                  <li key={stepIndex} className="flex items-start space-x-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-medium">
                      {stepIndex + 1}
                    </span>
                    <span className="text-gray-700 dark:text-gray-300 text-sm">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </div>

      {/* Contact Methods */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-center mb-8 text-gray-900 dark:text-white">
          Bizimle İletişime Geçin
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {contactMethods.map((method, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
              <method.icon className="h-12 w-12 mx-auto mb-4 text-gray-600 dark:text-gray-300" />
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                {method.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                {method.description}
              </p>
              <a
                href={method.href}
                className={`inline-block px-4 py-2 rounded-lg text-white font-medium transition-colors ${method.color}`}
              >
                {method.action}
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Help Categories */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-center mb-8 text-gray-900 dark:text-white">
          Yardım Konuları
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {helpCategories.map((category, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start space-x-4">
                <category.icon className="h-8 w-8 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                    {category.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                    {category.description}
                  </p>
                  <ul className="space-y-2">
                    {category.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <Link
                          to={link.href}
                          className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                        >
                          {link.text}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Popular Resources */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-900 dark:text-white">
          Popüler Kaynaklar
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/faq"
            className="flex items-center space-x-3 p-4 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow"
          >
            <QuestionMarkCircleIcon className="h-6 w-6 text-blue-600" />
            <span className="font-medium text-gray-900 dark:text-white">Sıkça Sorulan Sorular</span>
          </Link>
          <Link
            to="/terms"
            className="flex items-center space-x-3 p-4 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow"
          >
            <DocumentTextIcon className="h-6 w-6 text-purple-600" />
            <span className="font-medium text-gray-900 dark:text-white">Kullanım Koşulları</span>
          </Link>
          <Link
            to="/privacy"
            className="flex items-center space-x-3 p-4 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow"
          >
            <DocumentTextIcon className="h-6 w-6 text-green-600" />
            <span className="font-medium text-gray-900 dark:text-white">Gizlilik Politikası</span>
          </Link>
        </div>
      </div>
    </div>
  </div>
);

export default Help;
